import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { employeesQueryOptions, shortName } from '@/entities/employee'
import { excursionsQueryOptions } from '@/entities/excursion'
import { useSession } from '@/entities/session'
import { scheduleQueryOptions, type Task } from '@/entities/task'
import { timeBlocksQueryOptions } from '@/entities/time-block'
import { TaskDialog, type TaskDialogState } from '@/features/task-editor'
import {
  defaultBlockSlot,
  TimeBlockDialog,
  type TimeBlockDialogState,
} from '@/features/time-block-editor'
import {
  addDays,
  fmtDay,
  fmtWeekTitle,
  fromISODate,
  startOfDay,
  startOfWeek,
  toISODate,
} from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Icon } from '@/shared/ui/icon'
import { Segmented } from '@/shared/ui/segmented'
import { Spinner } from '@/shared/ui/spinner'
import {
  type BoardScope,
  type BoardView,
  DEFAULT_WINDOW,
  dayColumns,
  employeeColumns,
  FULL_DAY,
  fitWindow,
  ScheduleBoard,
  toBlocks,
  useNowMinute,
} from '@/widgets/schedule-board'

interface ScheduleSearch {
  date?: string
  view?: BoardView
  scope?: BoardScope
  full?: boolean
}

export const Route = createFileRoute('/_app/schedule')({
  validateSearch: (search: Record<string, unknown>): ScheduleSearch => ({
    ...(typeof search.date === 'string' ? { date: search.date } : {}),
    ...(search.view === 'week' || search.view === 'day' ? { view: search.view } : {}),
    ...(search.scope === 'me' || search.scope === 'team' ? { scope: search.scope } : {}),
    ...(search.full === true ? { full: true } : {}),
  }),
  component: SchedulePage,
})

function SchedulePage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { session } = useSession()
  const now = useNowMinute()

  // Стартовый вид — день: продукт отвечает на «кто когда занят», а это видно
  // только когда колонка — человек. Неделя остаётся линзой на свой график.
  const view: BoardView = search.view ?? 'day'
  // У сотрудника срез один — его собственный: чужую занятость ему не показывают
  // ни здесь, ни в ответе бэкенда, и переключателю нечего переключать.
  const teamWide = session?.isAdmin === true
  const scope: BoardScope = teamWide ? (search.scope ?? 'team') : 'me'
  const anchor = (search.date ? fromISODate(search.date) : null) ?? startOfDay(new Date())

  const from = view === 'week' ? startOfWeek(anchor) : startOfDay(anchor)
  const to = addDays(from, view === 'week' ? 7 : 1)

  const employeesQuery = useQuery(employeesQueryOptions())
  // Сетка показывает занятость: дела без времени в неё не попадают и живут
  // своим разделом.
  const scheduleQuery = useQuery(scheduleQueryOptions({ from, to, includeUntimed: false }))
  const excursionsQuery = useQuery(excursionsQueryOptions({ from, to, withBookings: false }))
  const timeBlocksQuery = useQuery(timeBlocksQueryOptions({ from, to }))

  const [taskDialog, setTaskDialog] = useState<TaskDialogState | null>(null)
  const [blockDialog, setBlockDialog] = useState<TimeBlockDialogState | null>(null)

  const employees = employeesQuery.data ?? []
  const mine = scope === 'me' && session ? [session.employeeId] : null

  const allBlocks = toBlocks(
    scheduleQuery.data?.items ?? [],
    excursionsQuery.data ?? [],
    timeBlocksQuery.data ?? [],
  )
  const blocks = mine
    ? allBlocks.filter((b) => b.ownerIds.some((id) => mine.includes(id)))
    : allBlocks

  const columns =
    view === 'week'
      ? dayColumns(anchor)
      : employeeColumns(
          anchor,
          (mine ? employees.filter((e) => mine.includes(e.id)) : employees).map((employee) => ({
            id: employee.id,
            title: shortName(employee),
            subtitle: employee.role || null,
          })),
        )

  const window = fitWindow(search.full ? FULL_DAY : DEFAULT_WINDOW, blocks, columns)

  const go = (next: Partial<ScheduleSearch>) => {
    void navigate({ search: (current) => ({ ...current, ...next }) })
  }

  const shift = (dir: 1 | -1) => {
    go({ date: toISODate(addDays(anchor, view === 'week' ? 7 * dir : dir)) })
  }

  const loading =
    employeesQuery.isLoading ||
    scheduleQuery.isLoading ||
    excursionsQuery.isLoading ||
    timeBlocksQuery.isLoading

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex flex-wrap items-center gap-x-2 gap-y-3 border-b px-3 py-2">
        <Button variant="secondary" size="sm" onClick={() => go({ date: toISODate(new Date()) })}>
          Сегодня
        </Button>
        <div className="flex items-center">
          <button
            type="button"
            aria-label="Назад"
            onClick={() => shift(-1)}
            className="p-2 text-text-tertiary hover:text-text-primary"
          >
            <Icon name="chevron-left" size={18} />
          </button>
          <button
            type="button"
            aria-label="Вперёд"
            onClick={() => shift(1)}
            className="p-2 text-text-tertiary hover:text-text-primary"
          >
            <Icon name="chevron-right" size={18} />
          </button>
        </div>
        <h1 className="t-h3 mr-auto min-w-0 truncate">
          {view === 'week' ? fmtWeekTitle(anchor) : fmtDay(anchor)}
        </h1>

        {/* Занять время — единственное действие расписания: сетка отвечает на
            «кто когда занят», и закрыть окно логично прямо здесь. Дела заводятся
            в своём разделе. Клик по пустому месту сетки ничего не создаёт: в поле
            по нему промахиваются пальцем. Вариант вторичный — расписание не
            форма с главным действием, а мелкий текст на акценте вдобавок не
            проходит по контрасту. */}
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            setBlockDialog({
              block: null,
              slot: defaultBlockSlot(anchor),
              employeeId: mine ? (mine[0] ?? null) : null,
            })
          }
        >
          <Icon name="plus" size={14} />
          занять время
        </Button>

        {/* Три независимых измерения — чей график, какой период, сколько часов
            по вертикали. Каждое отдельной группой: одним рядом равных подписей
            они читались как один переключатель на четыре положения. */}
        {teamWide && (
          <Segmented
            ariaLabel="Чей график"
            value={scope}
            onChange={(next) => go({ scope: next })}
            options={[
              { value: 'team', label: 'вся команда' },
              { value: 'me', label: 'только я' },
            ]}
          />
        )}
        <Segmented
          ariaLabel="Период"
          value={view}
          onChange={(next) => go({ view: next })}
          options={[
            { value: 'day', label: 'день' },
            { value: 'week', label: 'неделя' },
          ]}
        />
        {/* Часы окна — тоже выбор из двух, а не безымянная иконка часов:
            понять, что «часики» переключают охват суток, было неоткуда. */}
        <Segmented
          ariaLabel="Часы в сетке"
          value={search.full ? 'full' : 'work'}
          onChange={(next) => go({ full: next === 'full' })}
          options={[
            { value: 'work', label: 'рабочие' },
            { value: 'full', label: 'сутки' },
          ]}
        />
      </header>

      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col pt-3">
          <ScheduleBoard
            columns={columns}
            blocks={blocks}
            window={window}
            now={now}
            onOpen={(block) => {
              if (block.kind === 'excursion') {
                void navigate({
                  to: '/excursions/$excursionId',
                  params: { excursionId: block.entityId },
                })
                return
              }
              if (block.kind === 'time-block') {
                const found = timeBlocksQuery.data?.find((b) => b.id === block.entityId)
                if (found) setBlockDialog({ block: found, slot: found.slot, employeeId: null })
                return
              }
              const task = scheduleQuery.data?.items.find((t) => t.id === block.entityId)
              if (task) openTask(task, setTaskDialog)
            }}
          />
        </div>
      )}

      <TaskDialog
        state={taskDialog}
        employees={employees}
        excursions={excursionsQuery.data ?? []}
        day={anchor}
        onClose={() => setTaskDialog(null)}
      />
      <TimeBlockDialog
        state={blockDialog}
        employees={employees}
        selfId={session?.employeeId ?? null}
        onClose={() => setBlockDialog(null)}
      />
    </div>
  )
}

function openTask(task: Task, setDialog: (state: TaskDialogState) => void) {
  setDialog({
    task,
    slot: task.slot,
    assigneeIds: task.assigneeIds,
    excursionId: task.excursionId,
  })
}
